import { Button, DialogContent, Divider, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/material';
import { CustomDialogContainer } from '../../../t2c-fe/Common/dialog';
import CloseIcon from '@mui/icons-material/Close';
import { Fragment, useState } from 'react';
import { Styles } from '@t2c/shared-fe';
import { useGetPrivateAvatarData, useGetPublicAvatarData } from '../../../t2c-fe/hooks';
import { AvatarCard } from '../avatar-library/PrivateAvatarCard';
import {
  AvatarDataType,
  CardTypeEnum,
  ContentType,
  EMPTY_PAGE_TEXT,
  extractDate,
  extractTime,
  MediaStatus,
} from '../../../t2c-fe/utils';
import { Avatar } from '../../../t2c-fe/@types';
import EmptyPage from '../../../t2c-fe/Common/EmptyPage';
import InfiniteScroll from 'react-infinite-scroll-component';

interface Props {
  shouldOpenDialog: boolean;
  handleCloseDialog: () => void;
  handleOnClick: (data: AvatarDataType | Avatar) => void;
}

export const SelectAvatarModal: React.FC<Props> = ({
  shouldOpenDialog,
  handleCloseDialog,
  handleOnClick,
}) => {
  const tabs = {
    Public: 'Public Avatar',
    Provate: 'Your created avatars',
  };

  const [tab, setTab] = useState(tabs.Public);

  const handleTabClick = (tab: string) => {
    setTab(tab);
  };

  const [selectedAvatar, setSelectedAvatar] = useState(null);

  const {
    data: privateAvatars,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useGetPrivateAvatarData({
    filters: { status: MediaStatus.Success },
  });
  const { data: publicAvatars } = useGetPublicAvatarData();

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <CustomDialogContainer open={shouldOpenDialog} maxWidth={'md'}>
      <Box sx={styles.title}>
        <Box sx={{...styles.typeTab, flexDirection: isExtraSmall ? 'column' : 'row'}}>
          {Object.values(tabs).map((res, index) => (
            <Typography
              key={index}
              onClick={() => handleTabClick(res)}
              sx={{
                ...styles.tab,
                borderRightWidth: index !== Object.keys(tabs).length - 1 ? '1px' : '',
                color: tab === res ? (theme) => theme.palette.text.primary : '',
                backgroundColor: tab === res ? (theme) => theme.palette.background.activeCard : '',
                width: 200,
              }}
            >
              {res}
            </Typography>
          ))}
        </Box>
        <IconButton aria-label="close" onClick={handleCloseDialog} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider
        sx={{
          borderColor: (theme) => theme.palette.background.ternary,
        }}
      />
      <DialogContent dividers sx={styles.dialogContent}>
        {tab === tabs.Provate && !privateAvatars?.pages?.[0]?.data?.length && (
          <EmptyPage minimumHeight="30vh" text={EMPTY_PAGE_TEXT.avatar} type={ContentType.Avatar} />
        )}

        {tab === tabs.Provate && (
          <InfiniteScroll
            dataLength={
              privateAvatars?.pages?.reduce((acc, page) => acc + page?.data?.length, 0) || 0
            }
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={
              <Box p={2} textAlign={'center'}>
                <h2>loading...</h2>
              </Box>
            }
          >
            <Grid container spacing={4}>
              {privateAvatars?.pages?.map((page, pageIndex) => (
                <Fragment key={pageIndex}>
                  {!!page?.data?.length &&
                    page?.data?.map((res) => (
                      <Grid item flex={1} xs={12} sm={6} md={4} lg={3} key={res.id}>
                        <Box
                          sx={{
                            borderColor: (theme) => theme.palette.background.border,
                            borderWidth: selectedAvatar?.id === res.id ? 2 : 0,
                          }}
                        >
                          <AvatarCard
                            type={CardTypeEnum.Avatar}
                            avatarLogo={res.thumbnail}
                            avatarName={res.name}
                            message={res.errorMessage}
                            handleClick={() => setSelectedAvatar(res)}
                            status={res.status}
                            date={extractDate(res.updatedAt ? res.updatedAt : res.createdAt)}
                            time={extractTime(res.updatedAt ? res.updatedAt : res.createdAt)}
                            speechContentId={res.speechContentId}
                            creationId={res.id}
                            isImage={res.uploads.type === 'Image'}
                            isAvatarCreation={true}
                            refetchData={refetch}
                          />
                        </Box>
                      </Grid>
                    ))}
                </Fragment>
              ))}
            </Grid>
          </InfiniteScroll>
        )}

        {tab === tabs.Public && (
          <Grid container spacing={4}>
            {publicAvatars?.map((res) => (
              <Grid item flex={1} xs={12} sm={6} md={4} lg={3} key={res.id}>
                <Box
                  sx={{
                    borderColor: (theme) => theme.palette.background.border,
                    borderWidth: selectedAvatar?.id === res.id ? 2 : 0,
                  }}
                >
                  <AvatarCard
                    type={CardTypeEnum.Avatar}
                    avatarLogo={res.thumbnail}
                    avatarName={res.name}
                    handleClick={() => setSelectedAvatar(res)}
                    status={res.status}
                    date=""
                    time=""
                    isImage={res.uploads.type === 'Image'}
                    isPublic={true}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <Button
        sx={{
          width: 360,
          color: (theme) => theme.palette.text.primary,
          boxShadow: 'none',
          alignSelf: 'center',
          my: 2,
        }}
        variant="contained"
        onClick={() => handleOnClick(selectedAvatar)}
        disabled={!selectedAvatar}
      >
        Select
      </Button>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  modal: { width: '800px', margin: 'auto', height: '700px' },
  title: {
    padding: '24px',
    paddingTop: '10px',
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: (theme) => theme.palette.text.secondary,
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  theme: {
    width: '128px',
    paddingY: 2,
    backgroundColor: (theme) => theme.palette.background.selectedCardBorder,
    color: (theme) => theme.palette.common.white,
    textAlign: 'center',
  },
  clipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
  },
  saveBtn: {
    color: (theme) => theme.palette.common.white,
    boxShadow: 'none',
    width: '69px',
    height: '38px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  cancelBtn: {
    color: (theme) => theme.palette.common.black,
    borderColor: (theme) => theme.palette.background.dark,
    width: '69px',
    height: '38px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  btns: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1,
  },
  input: {
    backgroundColor: (theme) => theme.palette.background.card,
    marginBottom: 3,
  },
  closeButton: {
    color: (theme) => theme.palette.text.primary,
    borderColor: (theme) => theme.palette.background.border,
    mr: -1,
  },
  typeTab: {
    display: 'flex',
    borderRadius: '6px',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
    justifyContent: 'space-between',
    borderWidth: '1px',
  },
  tab: {
    flex: 1,
    textAlign: 'center',
    paddingY: 1,
    borderColor: (theme) => theme.palette.background.border,
    cursor: 'pointer',
  },
  clipsAndShowMore: {
    overflowY: 'auto',
    maxHeight: '350px',
    minHeight: '350px',
  },
  showMore: { width: '100%', textAlign: 'center' },
  genAiBtn: {
    color: (theme) => theme.palette.common.white,
    fontWeight: 400,
    borderRadius: 50,
  },
};
